import React from "react"
import PubSub from "pubsub-js"
import useT from "../hooks/useT"
import Spinner from "../ui/Spinner"
import PriceDisplay from "../ui/PriceDisplay"
import { INVOICE_REQUEST, PAY_AT_SHOP_KEY } from "./payment"
import { useCurrency } from "../store/currency"
import { LockClosedIcon } from "@heroicons/react/24/solid"
import Button from "aldoo-ra/Button"
import { hasPhysicalItems, isDigitalOnly } from "./cartItems"
import PaymentLogos from "./PaymentLogos"
import { useCartPaymentMethods } from "../ui/cart/paymentMethods"
import Value from "aldoo-ra/Value"
import { CHECKOUT_ALTERNATIVE_KEY } from "aldoo-ra/Store/elements/checkout-form"
import { BanknotesIcon } from "@heroicons/react/24/outline"

/**
 * Sticky total bar of the payment step (fragment "checkout-total-bar"): the
 * amount of the PaymentIntent and, for a digital-only cart, the "Need an
 * invoice?" link that opens the invoice-details step on demand (Continue there
 * returns to the payment). A cart that ships passed the address step, which
 * asks for the invoice itself.
 * On the left, what makes a new shop safe to pay: the payment goes through
 * Stripe, the card stays with Stripe, a parcel may be sent back within 14 days
 * (a download may not, once had), and the ways the form takes.
 */
export default function TotalBar({ store }) {
  const t = useT()
  const paymentInfo = store.paymentInfo
  // what the payment is in once there is one, else what the cart is priced in
  const { currency: cartIn } = useCurrency()
  const currency = (paymentInfo?.currency || cartIn).toLowerCase()
  // Only the payment says what is being charged: it is the server that prices
  // the basket, the delivery and the promo code. Showing the cart's own sum
  // first and replacing it a moment later made the total jump — shipping
  // appearing out of nowhere — so the bar waits for the figure it will charge.
  const cents = paymentInfo?.amount ?? null
  const askInvoice = isDigitalOnly(store.cart || [])
  const returnable = hasPhysicalItems(store.cart || [])
  const payment = useCartPaymentMethods(store.cart || [])
  // cash on delivery chosen on the step (PaymentChoice): no card to secure, the courier is paid
  const [alternative] = Value(CHECKOUT_ALTERNATIVE_KEY, null)
  const onDelivery = alternative?.id === "cash_on_delivery"
  const [atShop] = Value(PAY_AT_SHOP_KEY, false)

  const openInvoice = () => {
    // this checkout only: step data is cleared when the checkout closes
    PubSub.publish("PURCHASE_FLOW_SET_DATA", { stepId: INVOICE_REQUEST, data: true })
    PubSub.publish("PURCHASE_FLOW_SET_DATA", { stepId: "payment-return", data: true })
    PubSub.publish("PURCHASE_FLOW_GOTO", { stepId: "invoice-details" })
  }

  return (
    // the link sits on the left edge and the total on the right one of the
    // payment form above (checkout-form.jsx: px-2 md:px-6)
    <div className="sticky bottom-0 left-0 right-0 w-full min-h-[50px] flex items-center justify-between gap-3 px-2 md:px-6 py-2 bg-paper border-t-2 border-bordered z-[50]" style={{ boxShadow: "0 -4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
      {/* the lock with the title above, the marks under it from the left edge, and on a wide
          screen under them the card staying with Stripe and the 14 days to return */}
      {onDelivery ? (
        <div className="flex items-center gap-2 min-w-0" data-testid="cash-on-delivery-note">
          <BanknotesIcon className="w-5 h-5 shrink-0 text-success" aria-hidden="true" />
          <span className="flex flex-col min-w-0">
            <span className="text-[13px] md:text-[14px] font-semibold leading-tight">{atShop ? t`Pay at the shop` : t`Cash on delivery`}</span>
            <span className="text-[12px] text-text_secondary leading-tight">
              {atShop ? t`Pay on the spot at the shop` : t`You pay the courier when the parcel arrives`}
              {returnable && (
                <>
                  {" · "}
                  <Button variant="none" href="/returns-policy" target="_blank" rel="noopener" className="inline underline underline-offset-2 hover:text-primary">
                    {t`14 days to return`}
                  </Button>
                </>
              )}
            </span>
          </span>
        </div>
      ) : (
      <div className="flex flex-col gap-1 min-w-0" data-testid="secure-payment">
        <div className="flex items-center gap-1.5">
          <LockClosedIcon className="w-4 h-4 shrink-0 text-success" aria-hidden="true" />
          <span className="text-[13px] md:text-[14px] font-semibold leading-tight whitespace-nowrap">
            {t`Secure payment`}
            <span className="hidden md:inline"> {t`via Stripe`}</span>
          </span>
        </div>
        <PaymentLogos online={payment.online} cashOnDelivery={payment.cashOnDelivery} />
        <span className="hidden md:block text-[12px] text-text_secondary leading-tight whitespace-nowrap">
          {t`We never store your card details`}
          {returnable && (
            <>
              {" · "}
              <Button variant="none" href="/returns-policy" target="_blank" rel="noopener" className="inline underline underline-offset-2 hover:text-primary">
                {t`14 days to return`}
              </Button>
            </>
          )}
        </span>
      </div>
      )}
      <div className="flex flex-col md:flex-row items-end md:items-center gap-1 md:gap-5 shrink-0">
        {askInvoice && (
          <button type="button" onClick={openInvoice} className="text-sm text-primary underline underline-offset-2 hover:opacity-80 transition-opacity" data-testid="need-invoice">
            {t`Need an invoice?`}
          </button>
        )}
        <div className="flex items-center gap-2">
        {/* the total as the cart shows it (CartFooter): the same label, the same price */}
        <span className="text-[16px] font-semibold">{t`Total`}:</span>
        {cents === null ? (
          <Spinner size={20} className="w-auto py-0" />
        ) : (
          <span data-testid="checkout-total">
            <PriceDisplay amount={cents} currency={currency} variant="medium" className="min-w-0" />
          </span>
        )}
        </div>
      </div>
    </div>
  )
}
